import { defineStore } from "pinia";
import { ref } from "vue";
import { z } from "zod";

import { DialogOptions } from "@tableau/extensions-api-types";
import { validateSchema } from "@vm/validation";

import { TableauSettingsService } from "../services/tableau-settings-service";

export function createTableauSettingsEditorStore<T extends z.ZodType>(
  schema: T,
) {
  return defineStore("tableauSettingsEditor", () => {
    const initialParseResult = schema.safeParse({});
    const settings = ref(
      initialParseResult.success ? initialParseResult.data : ({} as z.infer<T>)
    );
    const invalidSettings = ref({} as z.core.$ZodIssue[]);
    const isInitialized = ref(false);
    const tableauSettingsService = new TableauSettingsService();
    const initialize = async () => {
      await tableauSettingsService.initializeEditor();
      const allSettings = await tableauSettingsService.getAllSettings();
      const parseResult = schema.safeParse(allSettings);

      if (!parseResult.success) {
        invalidSettings.value = parseResult.error.issues;
        settings.value = schema.parse({});
      } else {
        settings.value = parseResult.data;
      }
      isInitialized.value = true;
    };

    async function putSetting<K extends keyof z.infer<T>>(
      key: K,
      value: z.infer<T>[K],
    ): Promise<z.infer<T>[K]> {
      validateSchema(schema, { ...settings.value, [key]: value });
      const result = await tableauSettingsService.putSetting(
        key as string,
        value,
      );
      await tableauSettingsService.saveSettings();
      settings.value = { ...settings.value, [key]: result };
      return result;
    }

    async function putSettings(newSettings: Partial<z.infer<T>>) {
      validateSchema(schema, newSettings);
      const result = await tableauSettingsService.putSettings(newSettings);
      await tableauSettingsService.saveSettings();
      settings.value = { ...settings.value, ...result };
      return result;
    }

    function findSetting<K extends keyof z.infer<T>>(key: K): z.infer<T>[K] {
      return settings.value[key];
    }

    async function deleteSetting<K extends keyof z.infer<T>>(key: K) {
      await tableauSettingsService.deleteSetting(key as string);
      await tableauSettingsService.saveSettings();
      const { [key]: _, ...rest } = settings.value;
      settings.value = rest;
    }

    const displayDialog = (
      url: string,
      dialogOptions: {
        width: number;
        height: number;
        dialogStyle: "modal" | "modeless" | "window";
      },
    ) => {
      return tableauSettingsService.displayDialog(
        url,
        dialogOptions as DialogOptions,
      );
    };

    const closeDialog = () => {
      return tableauSettingsService.closeDialog();
    };

    return {
      settings,
      invalidSettings,
      isInitialized,
      initialize,
      putSetting,
      putSettings,
      findSetting,
      deleteSetting,
      displayDialog,
      closeDialog,
    };
  });
}
